/**
 * Copies text to the clipboard: a square icon button named "Copy" whose icon
 * turns to a check for two seconds, with "Copied" announced through a polite
 * live region. If the clipboard is unavailable (an insecure context, a denied
 * permission) it announces the failure instead of pretending it worked.
 */
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { IconCheck, IconCopy } from './icons'

export interface CopyButtonProps {
  text: string
  /** Accessible name. Default "Copy". */
  label?: string
  /** Announced after a copy. Default "Copied". */
  copiedLabel?: string
  size?: 32 | 40 | 48
  className?: string
}

type CopyState = 'idle' | 'copied' | 'failed'

const SIZE = { 32: 'h-8 w-8', 40: 'h-10 w-10', 48: 'h-12 w-12' } as const

export function CopyButton({ text, label = 'Copy', copiedLabel = 'Copied', size = 40, className }: CopyButtonProps) {
  const [state, setState] = useState<CopyState>('idle')
  const timer = useRef<number>()
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const copy = async () => {
    window.clearTimeout(timer.current)
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable')
      await navigator.clipboard.writeText(text)
      setState('copied')
    } catch (err) {
      console.warn('CopyButton: copy failed', err)
      setState('failed')
    }
    timer.current = window.setTimeout(() => setState('idle'), 2000)
  }

  return (
    <>
      <button
        type="button"
        aria-label={label}
        onClick={copy}
        className={clsx(
          'grid flex-none place-items-center text-[color:var(--icon-secondary)] transition-colors duration-110 ease-carbon hover:bg-layer-hover hover:text-text-primary',
          SIZE[size],
          className,
        )}
      >
        {state === 'copied' ? <IconCheck className="text-text-primary" /> : <IconCopy />}
      </button>
      <span aria-live="polite" className="sr-only">
        {state === 'copied' ? copiedLabel : state === 'failed' ? 'Copy failed. Select the text and copy it.' : ''}
      </span>
    </>
  )
}

export default CopyButton
